import { MouseEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Input, Typography } from 'antd'
import dayjs from 'dayjs'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingFilesState,
  listPointState,
  revalidatePointDetailState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { currentOpenMenu } from 'store/displayToggleStore'
import { BuildingFile } from 'types/building'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import DeleteIcon from 'components/icons/DeleteIcon'

type Props = {
  isSelected: boolean
  subImage: BuildingFile
  handleSelectSubImage: (file: BuildingFile) => void
}

export default function PointSubImage({
  isSelected,
  subImage,
  handleSelectSubImage
}: Props) {
  const { id: buildingId, floorId } = useParams()

  const [, setPointList] = useRecoilState(listPointState)
  const [selectedSubImage, setSelectedSubImage] = useRecoilState(
    selectedSubImageState
  )
  const [, setBuildingFiles] = useRecoilState(buildingFilesState)
  const [, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [currentSelectedPoint] = useRecoilState(selectedPoint)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const { isOpenRightMenu } = useRecoilValue(currentOpenMenu)

  const [isEditingNote, setIsEditingNote] = useState(false)
  const [newNote, setNewNote] = useState(subImage?.note || '')

  const { trigger } = useMutation(
    `v1/points/${currentSelectedPoint}/sub-images`
  )

  const { trigger: handleUpdateImageNote } = useMutation(
    `v1/points/${currentSelectedPoint}/images`
  )

  const { trigger: handleDeleteImageNote } = useMutation(
    `v1/points/${currentSelectedPoint}/images`,
    {},
    true
  )

  const saveNoteImage = async () => {
    if (newNote) {
      try {
        await handleUpdateImageNote({
          method: 'patch',
          data: {
            buildingId: Number(buildingId),
            fileId: subImage.id,
            note: newNote
          }
        })
        setIsEditingNote(false)
        setPointDetail((prev) =>
          prev
            ? {
                ...prev,
                subImages: prev.subImages?.map((item) =>
                  item.id === subImage.id ? { ...item, note: newNote } : item
                )
              }
            : prev
        )
      } catch (error) {
        setIsEditingNote(false)
      }
    } else {
      try {
        await handleDeleteImageNote({
          method: 'patch',
          data: {
            buildingId: Number(buildingId),
            fileId: subImage.id,
            note: ''
          }
        })
        setIsEditingNote(false)
        setPointDetail((prev) =>
          prev
            ? {
                ...prev,
                subImages: prev.subImages?.map((item) =>
                  item.id === subImage.id ? { ...item, note: '' } : item
                )
              }
            : prev
        )
      } catch (error) {
        setIsEditingNote(false)
      }
    }
  }

  const removeNoteImage = async () => {
    try {
      await handleDeleteImageNote({
        method: 'patch',
        data: {
          buildingId: Number(buildingId),
          fileId: subImage.id,
          note: ''
        }
      })
      setNewNote('')
    } catch (error) {
      /* eslint-disable no-console  */
    }
  }

  const handleDeleteSubimage = (e: MouseEvent<HTMLDivElement>, id: number) => {
    removeNoteImage()
    e.stopPropagation()
    trigger(
      {
        method: 'delete',
        data: null,
        query: [`${id}`],
        params: {
          building_id: +(buildingId || 0),
          floor_id: +(floorId || 0)
        }
      },
      {
        onSuccess: () => {
          const subImages = pointDetail?.subImages?.filter(
            (item) => item.id !== id
          )
          setPointList((prev) =>
            prev.map((item) =>
              item.id === currentSelectedPoint
                ? { ...item, isSubImages: Boolean(subImages?.length) }
                : item
            )
          )
          isOpenRightMenu &&
            setBuildingFiles((item) => ({
              ...item,
              files: item.files.map((image) =>
                image.id === id ? { ...image, pointId: null } : image
              )
            }))
          id === selectedSubImage && setSelectedSubImage(undefined)
          currentSelectedPoint === pointDetail?.id &&
            setRevalidatePointDetail(currentSelectedPoint)
        }
      }
    )
  }

  useEffect(() => {
    setIsEditingNote(false)
    setNewNote(
      pointDetail?.subImages?.find((item) => item.id === subImage.id)?.note ||
        ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubImage])

  const handleClickNote = () => {
    const placeHolder =
      pointDetail?.subImages?.find((item) => item.id === subImage.id)?.note ||
      (pointDetail?.subImages?.find((item) => item.id === subImage.id)
        ?.takenAt &&
        dayjs(
          pointDetail?.subImages?.find((item) => item.id === subImage.id)
            ?.takenAt
        )
          .utc()
          .format('YYYY-MM-DD HH:mm')) ||
      ''
    handleSelectSubImage(subImage)
    requestAnimationFrame(() => {
      setNewNote(placeHolder)
      setIsEditingNote(true)
    })
  }

  return (
    <div
      className={`min-h-[36px] p-[10px] flex border-0 border-b border-solid theme-border-secondary relative ${
        isSelected ? 'theme-bg-sub-primary' : 'theme-bg-third'
      }`}
      onClick={() => handleSelectSubImage(subImage)}
    >
      <LazyThumbnail
        imageType={ImageType.OBJECT_FILL}
        url={subImage.thumbnail || subImage.s3Path}
        name={subImage.name}
      />

      <div className="absolute top-3 right-3">
        <div
          className="cursor-pointer w-5 h-5 z-20 flex justify-center items-center"
          onClick={(e) => handleDeleteSubimage(e, subImage.id)}
        >
          <DeleteIcon />
        </div>
      </div>
      {!subImage?.note ? (
        <div className="ant-typography  text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px]">
          <Typography.Text
            className="text-[12px] text-white"
            onClick={(e) => {
              e.stopPropagation()
              handleClickNote()
            }}
          >
            {dayjs(subImage?.takenAt)
              ?.utc()
              .format('YYYY-MM-DD HH:mm')}
          </Typography.Text>
        </div>
      ) : (
        <div className="ant-typography text-white text-[12px] max-w-[180px] bg-[#02112070] absolute right-[10px] bottom-[10px]">
          <Typography.Text
            className="text-[12px] text-white truncate cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              handleClickNote()
            }}
            ellipsis={{ tooltip: subImage?.note }}
          >
            {subImage?.note}
          </Typography.Text>
        </div>
      )}
      {isEditingNote && (
        <div className="absolute bottom-0 right-2 w-[200px]">
          <Input
            className="w-full"
            value={newNote}
            onChange={(e) => {
              setNewNote(e.target.value)
            }}
            onBlur={() => {
              setIsEditingNote(false)
              setNewNote(subImage?.note || '')
            }}
            onPressEnter={saveNoteImage}
            autoFocus
          />
        </div>
      )}
    </div>
  )
}
